import * as React from "react";
import { Dialog } from "components/dialog";
import { formatMessage } from "lib/globalize-util";
import { merge, $ } from "glamor";
import theme from "styles/theme";
import { IAllocationDimension } from "helpers/allocation-dimension";
import { IDeliveryPurchaseOrder, IPurchaseOrderDoorGroup } from "helpers/delivery-purchase-order";
import Close from "components/icons/close";
import { DATATEST } from "constants/integration";
import { DeliveryPurchaseOrdersGrid } from "./delivery-purchase-orders-grid";

const styles = {
    actions: merge(
        $(" .applyButton", {
            marginLeft: "20px !important",
        })
    ),
    content: merge(
        $(" .manageContainer", {
            display: "flex",
            width: "100%",
        }),
        $(" .manageContainer > div", {
            display: "grid",
            width: "100%",
        }),
        $(" .modalDescription", {
            fontSize: "14px",
            color: theme.palette.darkGrey,
            marginBottom: "20px",
        })
    ),
    modalActions: merge(
        $(".actions", {
            alignItems: "center",
            display: "flex",
            justifyContent: "space-between",
            padding: "20px 0 !important",
        }),
        $(".actions + div", {
            overflowY: "scroll !important",
        }),
        $(".actions svg", {
            cursor: "pointer",
            fill: `${theme.palette.darkGrey} !important`,
            width: "16px !important",
            height: "16px !important",
        }),
        $(" .iconActions", {
            marginLeft: "10px",
            padding: "2px",
        }),
        $(" .modalTitle", {
            fontSize: "20px",
            margin: "0",
        }),
        $(" .titleRight", {
            textAlign: "right",
        }),
        $(" .isSavingText", {
            display: "inline-block",
            fontSize: "14px",
            marginRight: "20px",
            verticalAlign: "text-bottom",
        })
    ),
};

const muiStyles = {
    modalContainer: {
        width: "95%",
        height: "95%",
        maxWidth: "960px",
    },
};

interface IProps {
    parentAllocationDimension: IAllocationDimension;
    onToggleShow: () => void;
    mutateDeliveryPurchaseOrder: (deliveryPurchaseOrder: IDeliveryPurchaseOrder, index: number) => void;
    isSaving?: boolean;
    readOnly?: boolean;
    deliveryPurchaseOrders: IDeliveryPurchaseOrder[];
    merchantPurchaseOrderDoorGroups: IPurchaseOrderDoorGroup[];
    validatePOTable: boolean;
}
export const ManageDeliveryPurchaseOrder: React.FunctionComponent<IProps> = ({
    parentAllocationDimension,
    onToggleShow,
    mutateDeliveryPurchaseOrder,
    isSaving,
    readOnly,
    deliveryPurchaseOrders,
    merchantPurchaseOrderDoorGroups,
    validatePOTable,
}) => {
    const handleOnRowUpdated = React.useCallback((rowData, index) => {
        mutateDeliveryPurchaseOrder(rowData, index);
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const [disableEscapeKeyDown, setDisableEscapeKeyDown] = React.useState<boolean>(false);
    const handleCellEditing = React.useCallback((editMode) => setDisableEscapeKeyDown(editMode), []);

    const handleToggleShow = React.useCallback(() => {
        if (!disableEscapeKeyDown) {
            onToggleShow();
        } else {
            setDisableEscapeKeyDown(false);
        }
    }, [disableEscapeKeyDown]); // eslint-disable-line react-hooks/exhaustive-deps

    return (
        <Dialog
            modal={false}
            open={true}
            contentStyle={muiStyles.modalContainer}
            actionsContainerClassName={`${styles.actions}`}
            contentClassName={`${styles.content}`}
            onRequestClose={handleToggleShow}
            showCloseButton={false}
            disableEscapeKeyDown={disableEscapeKeyDown}
            title={
                <div {...styles.modalActions} className="actions">
                    <React.Fragment>
                        <div className="titleLeft">
                            <h4 className="modalTitle">{formatMessage("Define Purchase Orders")}</h4>
                        </div>
                        <div className="titleRight">
                            {isSaving && <div className="isSavingText">{formatMessage("saving changes")}</div>}
                            <Close
                                data-test={DATATEST.ASSORTMENTS.DETAILS_CLOSE_BTN}
                                key="close"
                                onClick={onToggleShow}
                                color={theme.palette.darkerGrey}
                            />
                        </div>
                    </React.Fragment>
                </div>
            }
        >
            <p className="modalDescription">
                {formatMessage("Define your Purchase Order attributes within this Assortment.")}
            </p>
            <DeliveryPurchaseOrdersGrid
                parentAllocationDimension={parentAllocationDimension}
                onRowUpdated={handleOnRowUpdated}
                readOnly={readOnly}
                onCellEditing={handleCellEditing}
                deliveryPurchaseOrders={deliveryPurchaseOrders}
                purchaseOrderDoorGroups={merchantPurchaseOrderDoorGroups}
                validatePOTable={validatePOTable}
            />
        </Dialog>
    );
};
